/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import axios from '../../api/axios';
import { BsGrid3X3GapFill } from 'react-icons/bs';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import { useApps } from '../../context/AppsContext';
import { useTranslation } from '../../hooks/useTranslation';
import { getApplicationCode } from '../../config/app.config';
import { AppsGrid } from './AppsGrid';
import './AppsMenuButton.css';

const getPlatformBaseUrl = (): string => {
  const env =
    (import.meta.env.VITE_PLATFORM_URL as string) ||
    (import.meta.env.VITE_BASE_URL as string) ||
    '';
  if (env) return env.replace(/\/+$/, '');
  if (typeof window !== 'undefined') return window.location.origin;
  return '';
};

export interface AppsMenuButtonProps {
  dropdownPortalTargetId?: string;
  /** Dropdown altına "Tüm Uygulamalar" footer linki. Verilmezse VITE_APPS_PAGE_PATH env'den okunur; yoksa gösterilmez. */
  footerHref?: string;
  footerLabel?: string;
}

function getAppsPagePath(): string {
  const env = (import.meta.env.VITE_APPS_PAGE_PATH as string || '').trim();
  return env || '/apps';
}

export function AppsMenuButton({ dropdownPortalTargetId, footerHref, footerLabel }: AppsMenuButtonProps) {
  const { language, userSettings, patchUserSettings } = useAuth();
  const { theme } = useTheme();
  const t = useTranslation(language);
  const { apps, getIconColor } = useApps();
  const [isOpen, setIsOpen] = useState(false);
  const currentAppCode = getApplicationCode();
  const triggerRef = useRef<HTMLDivElement>(null);
  const [bodyPortalStyle, setBodyPortalStyle] = useState<React.CSSProperties>({});

  // /me'den gelen appsOrder — ayrı API çağrısı gerektirmez
  const initialOrder = useMemo(() => {
    const order = userSettings?.appsOrder;
    return Array.isArray(order) ? order as string[] : undefined;
  }, [userSettings?.appsOrder]);

  // Sıralama kaydet → PATCH + context güncelle (sayfa yenilenmeden yansısın)
  const handleSyncOrder = useCallback((codes: string[]) => {
    axios.patch('/api/settings/user', { appsOrder: codes }).catch(() => {});
    patchUserSettings({ appsOrder: codes });
  }, [patchUserSettings]);

  // dropdownPortalTargetId verilmişse sayfadaki elementi bul (açılışta yeniden dene)
  const portalTarget = useMemo(() =>
    typeof document !== 'undefined' && dropdownPortalTargetId
      ? document.getElementById(dropdownPortalTargetId)
      : null,
  [dropdownPortalTargetId, isOpen]);

  // Dış portal yoksa document.body'e sabit konumlu portal açmak için pozisyon hesapla
  useLayoutEffect(() => {
    if (!isOpen || portalTarget || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    // Dropdown sağ kenarı: profil avatar dahil tüm action grubunun sağ kenarıyla hizalanır.
    // Üst container (profil + apps butonunu birlikte barındırır) baz alınır.
    const parentEl = triggerRef.current.parentElement;
    const alignRight = parentEl
      ? parentEl.getBoundingClientRect().right
      : rect.right;
    const GAP = 8;
    const BOTTOM_MARGIN = 12;
    const MAX_PANEL_HEIGHT = 520;
    const top = rect.bottom + GAP;
    const right = window.innerWidth - alignRight;
    const maxHeight = Math.min(MAX_PANEL_HEIGHT, window.innerHeight - top - BOTTOM_MARGIN);
    setBodyPortalStyle({
      position: 'fixed',
      top: `${top}px`,
      right: `${Math.max(right, 0)}px`,
      maxHeight: `${Math.max(maxHeight, 160)}px`,
    });
  }, [isOpen, portalTarget]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const inTrigger = target.closest('.apps-menu-trigger-container');
      const inDropdown = target.closest('.apps-dropdown');
      if (isOpen && !inTrigger && !inDropdown) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const baseUrl = getPlatformBaseUrl();
  const locale = (language || 'en').toLowerCase();
  const resolvedFooterHref = footerHref || getAppsPagePath() || '';
  const resolvedFooterLabel = footerLabel || t.common?.allApps || 'Tüm Uygulamalar';

  const appsGridContent = (
    <AppsGrid
      apps={apps}
      getLabel={(app) => app.label?.[locale] ?? app.label?.en ?? app.name}
      getTooltipDescription={(app) =>
        app.tooltipDescriptionLabel?.[locale]
        ?? app.tooltipDescriptionLabel?.en
        ?? app.tooltipDescription
        ?? app.description
        ?? app.name
      }
      getHref={baseUrl ? (app) => `${baseUrl}${app.path}` : undefined}
      theme={theme}
      getIconColor={getIconColor}
      currentAppCode={currentAppCode}
      onItemClick={() => setIsOpen(false)}
      columns={3}
      iconSize={24}
      role="menu"
      isManageable={true}
      initialOrder={initialOrder}
      onSyncOrder={handleSyncOrder}
      headerTitle={t.common?.appLauncher ?? 'Uygulamalar'}
      search={{
        label: t.common?.search ?? 'Ara',
        placeholder: t.common?.searchPlaceholder ?? 'Ara...',
        emptyLabel: t.headerSearch?.noResults ?? 'Sonuç bulunamadı',
      }}
      editLabel={t.common?.edit ?? 'Sıralamayı Düzenle'}
      cancelLabel={t.common?.cancel ?? 'İptal'}
      doneLabel={t.common?.save ?? 'Bitti'}
      footerHref={resolvedFooterHref ? `${baseUrl}${resolvedFooterHref}` : undefined}
      footerLabel={resolvedFooterLabel || undefined}
      footerOnClick={() => setIsOpen(false)}
    />
  );

  return (
    <>
      <div className="apps-menu-trigger-container" ref={triggerRef}>
        <button
          type="button"
          className="apps-grid-button"
          data-tour-id="app-launcher"
          onClick={() => setIsOpen((o) => !o)}
          aria-label={t.common?.appLauncher ?? 'Uygulamalar'}
          title={t.common?.appLauncher ?? 'Uygulamalar'}
          aria-expanded={isOpen}
        >
          <BsGrid3X3GapFill size={20} />
        </button>
      </div>
      {/* Dış portal (Core Layout pattern) */}
      {portalTarget && isOpen && createPortal(
        <div className="apps-dropdown" role="menu">{appsGridContent}</div>,
        portalTarget
      )}
      {/* Body portal (uydu uygulamalar — dış portal yoksa) */}
      {!portalTarget && isOpen && createPortal(
        <div className="apps-dropdown" role="menu" style={bodyPortalStyle}>{appsGridContent}</div>,
        document.body
      )}
    </>
  );
}
